import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";

import CKEditor from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

// Material-UI Core
import { makeStyles, withStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";

// Material-UI Icon
import Button from "@material-ui/core/Button";

// Custom Component
import Legend from "../Legend/Legend";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import SaveWarnDialog from "../Dialog/SaveWarnDialog";
import FullScreenDialog from "../Dialog/FullScreenDialog";
import FileDropzone from "../FileDropzone/FileDropzone";
import CirculationFileZoneContent from "./CirculationFileZoneContent";
import FilePreview from "../FilePreview/FilePreview";
import LockScreenLoading from "../LockScreenLoading/LockScreenLoading";

// Custom Style
import {
  redColor,
  oceanBlueColor,
  grayColor,
} from "../../styles/jss/cmsMainStyle";
import { circulationReplyFormStyle } from "../../styles/jss/components/circulation/circulationReplyFormStyle";

// Global Function
import { ckEditorToolbar } from "../../global/ckEditor";
import { webViewerSaveAnnotation } from "../../global/webViewerSaveAnnotation";

// Fetch Data
import { replyCirculation } from "../../dataSource/circulation";
import { fileUpload } from "../../dataSource/fileManager";

// Custom Style (Typography)
const CircCodeTypography = withStyles({
  root: {
    fontSize: 18,
    fontWeight: 600,
    color: oceanBlueColor[11],
  },
})(Typography);

const MsgTitleTypography = withStyles({
  root: {
    fontSize: 18,
    fontWeight: 600,
    color: grayColor[14],
  },
})(Typography);

const useStyles = makeStyles((theme) => circulationReplyFormStyle(theme));
const store = require("store");

const CirculationReplyForm = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    presetFiles,
    openFileManagerDialog,
    circulationDetail,
    setCirculationDetail,
    closeCirculationDialog,
    renewCircList,
    setRenewCircList,
    buildAlert,
  } = props;

  const circulationDataTemplate = {
    circ_subject: "",

    recipients: {
      CC: [],
      TO: [],
    },

    messages: {
      parent_seq: 0,
      content: "",
      dms_property_seqs: [],
      files: [],
    },
  };

  // Redux Data
  const circulationDataRedux = useSelector(
    (state) => state.circulationDataRedux
  );
  const propertySeqRedux = useSelector((state) => state.propertySeqRedux);
  const userModulesRedux = useSelector((state) => state.userModulesRedux);
  const [lockScreenLoadingOpen, setLockScreenLoadingOpen] = useState(false);
  const [progressTitle, setProgressTitle] = useState("Sending");

  const [editorValue, setEditorValue] = useState("");
  const [filePreviewProperty, SetFilePreviewProperty] = useState(null);
  const [filePreviewDialogOpen, setFilePreviewDialogOpen] = useState(false);
  const [circulationData, setCirculationData] = useState(
    circulationDataTemplate
  );
  const [validationErrors, setValidationErrors] = useState({});

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState({
    dialogTitle: "",
    dialogContent: "",
    confirmAction: "",
    cancelAction: "",
  });

  const [saveWarnDialogOpen, setSaveWarnDialogOpen] = useState(false);
  const [saveWarnDialogData, setSaveWarnDialogData] = useState({
    dialogTitle: "",
    dialogContent: "",
    saveAction: "",
    dontSaveAction: "",
    cancelAction: "",
  });

  // CKEditor Config
  const editorConfiguration = {
    // plugins: [ Essentials, Bold, Italic, Paragraph ],
    toolbar: ckEditorToolbar,
  };

  // Build Confirm Dialog
  const confirmDialogHandleOpen = (data) => {
    setConfirmDialogData({
      dialogTitle: data.dialogTitle,
      dialogContent: data.dialogContent,
      confirmAction: data.confirmAction,
      cancelAction: data.cancelAction,
    });

    setConfirmDialogOpen(true);
  };

  const confirmDialogHandleClose = () => {
    setConfirmDialogOpen(false);
  };

  // Build Save Warn Dialog
  const saveWarnDialogHandleOpen = (data) => {
    setSaveWarnDialogData({
      dialogTitle: data.dialogTitle,
      dialogContent: data.dialogContent,
      saveAction: data.saveAction,
      dontSaveAction: data.dontSaveAction,
      cancelAction: data.cancelAction,
    });

    setSaveWarnDialogOpen(true);
  };

  const saveWarnDialogHandleClose = () => {
    setSaveWarnDialogOpen(false);
  };

  const filePreviewDialogHandleOpen = () => {
    setFilePreviewDialogOpen(true);
  };

  const filePreviewDialogHandleClose = () => {
    if (!store.get("annotActive")) {
      setFilePreviewDialogOpen(false);
    } else {
      alertSaveActionHandleOpen();
    }
  };

  // Handle Form Input
  const ckeditorHandleChange = (event, editor) => {
    const inputData = editor.getData();
    const data = { ...circulationData };

    setEditorValue(inputData);
    data.messages.content = inputData;
    setCirculationData({ ...circulationData, ...data });
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: { ...circulationData, ...data },
    });

    if (
      validationErrors.content && validationErrors.content !== "" ? true : false
    ) {
      validate({ ...circulationData, content: inputData });
    }
  };

  const alertSaveActionHandleOpen = () => {
    saveWarnDialogHandleOpen({
      dialogTitle: "Annotation Change Warning",
      dialogContent: "Do you want to save the change of annotation.",
      saveAction: alertSaveAnnotation,
      dontSaveAction: alertDontSaveAnnotation,
      cancelAction: alertSaveActionHandleClose,
    });
  };

  const alertSaveActionHandleClose = () => {
    saveWarnDialogHandleClose();
  };

  const alertSaveAnnotation = () => {
    annotationSaveAction();

    alertSaveActionHandleClose();
    setFilePreviewDialogOpen(false);
  };

  const alertDontSaveAnnotation = () => {
    alertSaveActionHandleClose();
    setFilePreviewDialogOpen(false);
  };

  const annotationSaveActionHandleOpen = () => {
    confirmDialogHandleOpen({
      dialogTitle: "Annotation Save",
      dialogContent: "Are you sure to save the annotation.",
      confirmAction: annotationSaveAction,
      cancelAction: annotationSaveActionHandleClose,
    });
  };

  const annotationSaveActionHandleClose = () => {
    confirmDialogHandleClose();
  };

  const annotationSaveAction = () => {
    webViewerSaveAnnotation(
      filePreviewProperty.fileCategory,
      filePreviewProperty.fileSequence
    ).then((res) => {
      annotationSaveActionHandleClose();

      if (res.type === "success") {
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "success",
          title: "Success Alert",
          content: "The Annotation have been save",
        });
      } else {
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "error",
          title: "Error Alert",
          content: res.message,
        });
      }
    });
  };

  // Main Action
  const fileUploadAction = (fileType, acceptedFilesPool) => {
    acceptedFilesPool.map((file) => {
      fileUpload(file).then((res) => {
        console.log(
          "*****************************************************************************"
        );
        console.log("Temporary upload File Successful");
        console.log(res);
        console.log(
          "*****************************************************************************"
        );

        buildFileRecord(fileType, res.data[0]);
      });
    });
  };

  const buildFileRecord = (fileType, fileUploadRecord) => {
    const val = { ...circulationData };
    fileUploadRecord.modifydate = "";
    val.messages.files.push(fileUploadRecord);

    setCirculationData({ ...circulationData, ...val });
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: { ...circulationData, val },
    });
  };

  const viewFileAction = (fileCat, fileSeq) => {
    SetFilePreviewProperty({
      fileCategory: fileCat,
      fileSequence: fileSeq,
    });

    filePreviewDialogHandleOpen();
  };

  const removeFileConfirmAction = (fileRecord) => {
    confirmDialogHandleOpen({
      dialogTitle: "Circulation attachment file remove",
      dialogContent: "Are you sure to remove the attachment file.",
      confirmAction: () => removeFileAction(fileRecord),
      cancelAction: removeFileCancelAction,
    });
  };

  const removeFileCancelAction = () => {
    confirmDialogHandleClose();
  };

  const removeFileAction = (fileRecord) => {
    let result;
    let data = { ...circulationData };

    result = [...circulationData.messages.files].filter(
      (res) => res !== fileRecord
    );
    data.messages.files = result;

    setCirculationData({ ...circulationData, ...data });
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: { ...circulationData, ...data },
    });

    confirmDialogHandleClose();
  };

  const sendReplyCirculationConfirmAction = () => {
    if (validate()) {
      confirmDialogHandleOpen({
        dialogTitle: "Circulation Reply",
        dialogContent: "Are you sure to reply the circulation.",
        confirmAction: () => sendReplyCirculation(),
        cancelAction: sendReplyCirculationCancelAction,
      });
    }
  };

  const sendReplyCirculationCancelAction = () => {
    confirmDialogHandleClose();
  };

  const sendReplyCirculation = () => {
    confirmDialogHandleClose();
    setLockScreenLoadingOpen(true);
    replyCirculation(circulationDetail.circ_seq, circulationData.messages).then(
      (res) => {
        console.log(
          "*****************************************************************************"
        );
        console.log("System Module: Circulation");
        console.log("Message: Reply Circulation successful sent.");
        console.log(res);
        console.log(
          "*****************************************************************************"
        );

        setTimeout(() => {
          setLockScreenLoadingOpen(false);
          setRenewCircList(renewCircList + 1);
          setCirculationDetail(null);
          resetCirculationData();
          closeCirculationDialog();
          buildAlert({
            // Alert Type: success, info, warning, error
            type: "success",
            title: "Success Alert",
            content: "The Circulation have been reply",
          });
        }, 2000);
      }
    );
  };

  const resetCirculationData = () => {
    setCirculationData(circulationDataTemplate);
    dispatch({
      type: "CREATE_CIRCULATION_DATA",
      data: null,
    });
  };

  // Dialog Button
  let circulationAttachmentPreviewDialogBtn = [];
  if (userModulesRedux !== null) {
    userModulesRedux.map((module) => {
      if (module.path === "/circulation") {
        if (module.rights.modifiable || module.rights.administrable) {
          circulationAttachmentPreviewDialogBtn = [
            {
              buttonName: "Annotation Save",
              buttonFunction: annotationSaveActionHandleOpen,
              chooseFile: false,
            },
          ];
        }
      }
    });
  }

  // Input Validation
  const validate = (fieldValues = circulationData) => {
    let temp = { ...validationErrors };

    // if ("content" in fieldValues.messages) {
    // 	temp.content = fieldValues.messages.content ? "" : "This field is required"
    // }

    setValidationErrors({ ...temp });

    if (fieldValues == circulationData) {
      return Object.values(temp).every((x) => x == "");
    }
  };

  useEffect(() => {
    if (presetFiles !== null) {
      let data = { ...circulationData };

      data.messages.dms_property_seqs = propertySeqRedux;

      data.messages.files.push(...presetFiles);
      setCirculationData({ ...circulationData, ...data });
      dispatch({
        type: "CREATE_CIRCULATION_DATA",
        data: { ...circulationData, ...data },
      });
    }
  }, [presetFiles]);

  return (
    // console.log("Circulation Detail Source Data | Variable: circulationDetail (CirculationReplyForm.js)"),
    // console.log(circulationDetail),

    // console.log("Circulation Reply Data | Variable: circulationData (CirculationReplyForm.js)"),
    // console.log(circulationData),

    // console.log("Circulation Reply Data (Redux) | Variable: circulationDataRedux (CirculationReplyForm.js)"),
    // console.log(circulationDataRedux),

    // console.log("CKEditor Value | Variable: editorValue (CirculationReplyForm.js)"),
    // console.log(editorValue),

    // console.log("---------------------------------------------------------------------------------"),

    <Fragment>
      <LockScreenLoading
        open={lockScreenLoadingOpen}
        circleSize={200}
        progressTitle={progressTitle}
      />
      <ConfirmDialog
        open={confirmDialogOpen}
        dialogTitle={confirmDialogData.dialogTitle}
        dialogContent={confirmDialogData.dialogContent}
        confirmAction={confirmDialogData.confirmAction}
        cancelAction={confirmDialogData.cancelAction}
      />

      <SaveWarnDialog
        open={saveWarnDialogOpen}
        dialogTitle={saveWarnDialogData.dialogTitle}
        dialogContent={saveWarnDialogData.dialogContent}
        saveAction={saveWarnDialogData.saveAction}
        dontSaveAction={saveWarnDialogData.dontSaveAction}
        cancelAction={saveWarnDialogData.cancelAction}
      />

      <FullScreenDialog
        open={filePreviewDialogOpen}
        dialogTitle={"Circuration Attachment File Preview"}
        formContent={<FilePreview filePreviewProperty={filePreviewProperty} />}
        button={circulationAttachmentPreviewDialogBtn}
        handleClose={filePreviewDialogHandleClose}
      />

      <div className={classes.root}>
        {circulationDetail !== null ? (
          <Fragment>
            <div className={classes.messageCode}>
              <CircCodeTypography>
                {circulationDetail.circ_code}
              </CircCodeTypography>
            </div>

            <div className={classes.messageSubject}>
              <MsgTitleTypography>
                {circulationDetail.circ_subject}
              </MsgTitleTypography>
            </div>

            <Divider className={classes.divider} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Legend
                  id="attachment"
                  label="ATTACHMENT"
                  normalTitleColor={grayColor[10]}
                  normalBorderColor={grayColor[4]}
                  messageTitleColor={redColor[6]}
                  messageBorderColor={redColor[6]}
                >
                  <Box className={classes.addFileBtnArea}>
                    <FileDropzone
                      key={"circulation_Attachment"}
                      title={""}
                      fileType={"circulation_Attachment"}
                      disabled={false}
                      fileUploadAction={fileUploadAction}
                      fileRemoveAction={removeFileConfirmAction}
                      viewFileAction={viewFileAction}
                      DropzoneContent={CirculationFileZoneContent}
                      openFileManagerDialog={openFileManagerDialog}
                    />
                  </Box>
                </Legend>
              </Grid>

              <Grid item xs={12}>
                <Box
                  border={
                    validationErrors.content && validationErrors.content !== ""
                      ? 1
                      : 0
                  }
                  borderColor={
                    validationErrors.content && validationErrors.content !== ""
                      ? "error.main"
                      : "grey.500"
                  }
                >
                  <CKEditor
                    editor={ClassicEditor}
                    data={editorValue}
                    onChange={ckeditorHandleChange}
                    config={editorConfiguration}
                  />
                </Box>
                {validationErrors.content && validationErrors.content !== "" ? (
                  <Typography
                    variant="caption"
                    color="error"
                    className={classes.errorMsg}
                  >
                    {validationErrors.content}
                  </Typography>
                ) : (
                  ""
                )}
              </Grid>
            </Grid>
          </Fragment>
        ) : (
          ""
        )}
      </div>

      <div className={classes.btnArea}>
        <Button
          variant="contained"
          color="primary"
          onClick={sendReplyCirculationConfirmAction}
        >
          Send
        </Button>
      </div>
    </Fragment>
  );
};

export default CirculationReplyForm;
